import { useEffect, useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { useCase } from './hooks/useCase';
import { useSession } from './hooks/useSession';
import { useTranscript } from './hooks/useTranscript';
import { useZoomContext, type ZoomContext } from './hooks/useZoomContext';
import { startRtms, stopRtms, subscribeRtmsStatus } from './lib/zoom';
import { AgreementPage } from './pages/AgreementPage';
import { BriefingPage } from './pages/BriefingPage';
import { LiveSessionPage } from './pages/LiveSessionPage';
import { DashboardApp } from './pages/dashboard/DashboardApp';
import { DemoPlayer } from './components/DemoPlayer';
import { EmptyState } from './components/EmptyState';
import { ErrorState, LoadingState, SignInPrompt } from './components/States';
import type { Case } from './types/case';
import type { Session } from './api/mediatorService';

const DEMO_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEMO === 'true';

type Tab = 'briefing' | 'live' | 'agreement';

export function App() {
  const zoom = useZoomContext();

  if (zoom.status === 'loading') return <LoadingState label="Connecting to Zoom…" />;
  if (zoom.status === 'error') {
    return (
      <ErrorState
        title="Couldn't start the Zoom integration"
        message={zoom.error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  // Render-mode switch: only a live meeting gets the sidebar. The Zoom main
  // client and ordinary browser tabs get the Levelheaded dashboard (Mission
  // Control), which authenticates with the mediator's Levelheaded account rather
  // than Zoom OAuth.
  if (zoom.context.runningContext !== 'inMeeting') {
    return <DashboardApp inZoomClient={zoom.context.runningContext !== 'browser'} />;
  }

  return <SidebarApp context={zoom.context} />;
}

function SidebarApp({ context }: { context: ZoomContext }) {
  const auth = useAuth();

  useEffect(() => {
    void subscribeRtmsStatus((event) => {
      console.log('[rtms-status]', event);
    });
  }, []);

  if (auth.state.status === 'exchanging') {
    return <LoadingState label="Signing you in…" />;
  }
  if (auth.state.status === 'error') {
    return (
      <ErrorState
        title="Sign-in failed"
        message={auth.state.error}
        onRetry={() => void auth.signIn()}
      />
    );
  }

  const authSession = auth.state.session;
  if (!authSession) return <SignInPrompt onSignIn={() => void auth.signIn()} />;

  return (
    <AuthenticatedApp
      meetingId={context.meetingId}
      runningContext={context.runningContext}
      accessToken={authSession.accessToken}
      userId={authSession.userId}
    />
  );
}

function AuthenticatedApp({
  meetingId,
  runningContext,
  accessToken,
  userId,
}: {
  meetingId: string | null;
  runningContext: string;
  accessToken: string;
  userId: string | null;
}) {
  const { state: caseState, updateSession } = useCase(meetingId, accessToken);

  if (!meetingId) return <EmptyState meetingId={null} runningContext={runningContext} />;
  if (caseState.status === 'loading') return <LoadingState label="Loading case briefing…" />;
  if (caseState.status === 'unlinked') {
    return <EmptyState meetingId={meetingId} runningContext={runningContext} />;
  }
  if (caseState.status === 'error') {
    return (
      <ErrorState
        title="Couldn't load the case"
        message={caseState.error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <ActiveSessionShell
      case={caseState.case}
      session={caseState.session}
      meetingId={meetingId}
      accessToken={accessToken}
      userId={userId}
      onSessionChange={updateSession}
    />
  );
}

function ActiveSessionShell({
  case: c,
  session: initialSession,
  meetingId,
  accessToken,
  userId,
  onSessionChange,
}: {
  case: Case;
  session: Session;
  meetingId: string;
  accessToken: string;
  userId: string | null;
  onSessionChange: (s: Session) => void;
}) {
  const [activeTab, setActiveTab] = useState<Tab>(() =>
    initialSession.status === 'in_progress' ? 'live' : 'briefing',
  );

  const session = useSession({
    caseId: c.id,
    meetingId,
    initialSession,
    accessToken,
    userId,
  });

  // Mount the live feed hook here (above the tab switch) so the WebSocket
  // stays open and the in-memory transcript / suggestions / moments persist
  // when the mediator flips between Briefing and Live.
  const feed = useTranscript(initialSession.id, accessToken);

  // Keep the parent useCase state in sync with lifecycle changes from useSession.
  useEffect(() => {
    if (session.state.status === 'idle' && session.state.session) {
      onSessionChange(session.state.session);
    }
  }, [session.state, onSessionChange]);

  const currentSession: Session =
    session.state.status === 'idle' || session.state.status === 'error'
      ? (session.state.session ?? initialSession)
      : initialSession;

  const working = session.state.status === 'working';
  const mutationError = session.state.status === 'error' ? session.state.error : undefined;

  const handleStart = async () => {
    try {
      await session.start();
    } catch {
      /* error surfaces via session.state */
      return;
    }
    // Tell Zoom to start RTMS on this meeting; the `meeting.rtms_started`
    // webhook will then hit our backend and spin up the DO pipeline.
    try {
      console.log('[app] calling zoomSdk.startRTMS()');
      await startRtms();
      console.log('[app] startRTMS resolved');
    } catch (err) {
      // Transcription auto-starts at meeting start now — a rejected manual
      // start almost always means "already running". Log, never alarm.
      console.warn('[app] startRTMS rejected (likely already running)', err);
    }
    setActiveTab('live');
  };

  const handleEnd = async () => {
    // Stop RTMS first so Zoom fires the webhook and the DO tears down
    // cleanly — then record the status change server-side.
    try {
      await stopRtms();
    } catch (err) {
      console.error('[app] stopRTMS failed', err);
    }
    try {
      await session.end();
      setActiveTab('briefing');
    } catch {
      /* error surfaces via session.state */
    }
  };

  const handleEndUnresolved = async (reason?: string) => {
    try {
      await stopRtms();
    } catch (err) {
      console.error('[app] stopRTMS failed', err);
    }
    try {
      await session.endUnresolved(reason);
      setActiveTab('briefing');
    } catch {
      /* error surfaces via session.state */
    }
  };

  const canGoLive =
    currentSession.status === 'consenting' ||
    currentSession.status === 'in_progress' ||
    Boolean(currentSession.consent_given_at);

  return (
    <div className="flex h-full flex-col">
      {DEMO_ENABLED && (
        <DemoPlayer sessionId={currentSession.id} accessToken={accessToken} />
      )}
      <nav className="flex shrink-0 border-b border-surface-border bg-white">
        <TabButton
          active={activeTab === 'briefing'}
          onClick={() => setActiveTab('briefing')}
          label="📋 Briefing"
        />
        <TabButton
          active={activeTab === 'live'}
          onClick={() => canGoLive && setActiveTab('live')}
          disabled={!canGoLive}
          label="🎙️ Live"
        />
        <TabButton
          active={activeTab === 'agreement'}
          onClick={() => setActiveTab('agreement')}
          label="📝 Agreement"
        />
      </nav>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {activeTab === 'briefing' && (
          <BriefingPage
            sessionId={currentSession.id}
            meetingId={meetingId}
            accessToken={accessToken}
            case={c}
            sessionStatus={currentSession.status}
            phoneJoin={(currentSession.metadata?.phone_join as import('./api/mediatorService').PhoneJoin | undefined) ?? null}
            onStartSession={handleStart}
            starting={working}
            startError={mutationError}
          />
        )}
        {activeTab === 'live' && (
          <LiveSessionPage
            case={c}
            session={currentSession}
            accessToken={accessToken}
            feed={feed}
            onEnd={handleEnd}
            onEndUnresolved={handleEndUnresolved}
            ending={working}
          />
        )}
        {activeTab === 'agreement' && (
          <AgreementPage
            caseId={c.id}
            sessionId={currentSession.id}
            accessToken={accessToken}
            property={c}
          />
        )}
      </div>

    </div>
  );
}

function TabButton({
  active,
  onClick,
  disabled,
  label,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        'flex-1 px-3 py-2 text-xs font-medium transition-colors',
        active
          ? 'border-b-2 border-brand text-brand'
          : 'border-b-2 border-transparent text-gray-600 hover:text-gray-900',
        disabled ? 'cursor-not-allowed opacity-40 hover:text-gray-600' : '',
      ].join(' ')}
    >
      {label}
    </button>
  );
}
